import React from "react";

export interface ErrorBoundaryProps {
	children: React.ReactNode;
	/** Optional custom fallback render function */
	fallback?: React.ReactNode;
	/** Optional logging hook for external error tracking (e.g. Sentry) */
	onErrorCapture?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

class ErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	public state: ErrorBoundaryState = {
		hasError: false,
		error: null
	};

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo): void {
		// Always log to console in development for easier debugging
		// eslint-disable-next-line no-console
		console.error("Uncaught error in ErrorBoundary:", error, info);

		if (this.props.onErrorCapture) {
			this.props.onErrorCapture(error, info);
		}
	}

	private handleReload = (): void => {
		// Full reload is the safest way to recover from an unknown fatal error
		window.location.reload();
	};

	render(): React.ReactNode {
		const { children, fallback } = this.props;

		if (!this.state.hasError) {
			return children;
		}

		// If a custom fallback was provided, use it
		if (fallback) {
			return fallback;
		}

		return (
			<div
				style={{
					padding: "24px",
					fontFamily:
						"system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
					color: "#ffffff",
					backgroundColor: "#121212",
					minHeight: "100vh",
					boxSizing: "border-box",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					textAlign: "center"
				}}
			>
				<h1 style={{ fontSize: "22px", marginBottom: "12px" }}>
					Something went wrong
				</h1>
				<p style={{ marginBottom: "16px", maxWidth: 480 }}>
					An unexpected error occurred while loading this page. Please try
					reloading. If the problem continues, contact the support team.
				</p>
				{this.state.error && (
					<div
						style={{
							marginTop: "16px",
							padding: "12px",
							backgroundColor: "#1e1e1e",
							borderRadius: "4px",
							fontSize: "13px",
							whiteSpace: "pre-wrap",
							maxWidth: 640
						}}
					>
						<strong>Technical details:</strong>
						<br />
						{this.state.error.toString()}
					</div>
				)}
				<button
					style={{
						marginTop: "20px",
						padding: "8px 16px",
						borderRadius: "4px",
						border: "none",
						cursor: "pointer",
						backgroundColor: "#ffb300",
						color: "#121212",
						fontWeight: 600
					}}
					onClick={this.handleReload}
				>
					Reload
				</button>
			</div>
		);
	}
}

export default ErrorBoundary;
